'use client';

import { SignInButton, useAuth } from '@clerk/nextjs';
import { type TheBoatDBConfigType, useConfig } from '@marine/configs';
import styles from '@styles/ApiKeys/ApiKeys.module.scss';
import { Button, Skeleton } from 'antd';
import { ApiKeysManager } from './ApiKeysManager';

/**
 * The Clerk gate around the key screen. Key management is authenticated by the
 * session on every request — this only decides what the page renders, and is
 * not what protects the keys themselves.
 */
const ApiKeysScreen: React.FC = () => {
  const { isLoaded, isSignedIn } = useAuth();
  const { theboatDbUrl } = useConfig<TheBoatDBConfigType>();

  // Absolute, because sign-in happens on the primary domain and has to be told
  // where to come back to.
  const returnUrl = `${theboatDbUrl ?? ''}/account/api-keys`;

  if (!isLoaded) {
    return (
      <div className={styles.page}>
        <div className={`${styles.panel} ${styles['panel--padded']}`}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className={styles.page}>
        <div className={`${styles.panel} ${styles.empty}`}>
          <p className={styles.empty__title}>Sign in to manage your API keys</p>
          <p className={styles.empty__text}>
            API keys belong to your account, so you need to be signed in to create, view or revoke them.
          </p>
          <SignInButton forceRedirectUrl={returnUrl} fallbackRedirectUrl={returnUrl}>
            <Button color="primary" variant="solid">
              Sign in
            </Button>
          </SignInButton>
        </div>
      </div>
    );
  }

  return <ApiKeysManager />;
};

export { ApiKeysScreen };
